import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { UploadService } from 'src/app/core/services/upload.service';

interface SelectedFile {
  file: File;
  preview: string;
  name: string;
  size: number;
  type: string;
  comment: string;
}

export interface FileManagerResponse {
  fileId: number;
  fileName: string;
  filePath: string;
  fileType: string;
  fileSize: number;
  fileExt?: string;
  description?: string;
  owner?: number;
  createDate?: string;
  createUser?: string;
  lastUpdDate?: string;
  lastUpdUser?: string;
  resizePath?: string;
  scope?: string;
  sockId?: number;
}

export interface UploadApiResponse {
  data: FileManagerResponse;
  message?: string;
  code?: string;
}

type Privacy = 'public' | 'friends' | 'private';

@Component({
  selector: 'app-modal-upload',
  templateUrl: './modal-upload.component.html',
  styleUrls: ['./modal-upload.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalUploadComponent implements OnDestroy {
  @Output() fileUploaded = new EventEmitter<FileManagerResponse[]>();
  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

  selectedFiles: SelectedFile[] = [];
  isVisible = false;
  selectedPrivacy: Privacy | null = null;
  uploading = false;
  errorMessage = '';

  constructor(
    private uploadService: UploadService,
    private cdr: ChangeDetectorRef
  ) {}

  open(): void {
    if (this.isVisible) return;

    this.isVisible = true;
    this.errorMessage = '';
    document.body.classList.add('overflow-hidden');
    this.cdr.markForCheck();
  }

  close(): void {
    if (!this.isVisible) return;

    this.isVisible = false;
    this.selectedFiles = [];
    this.selectedPrivacy = null;
    this.uploading = false;
    this.errorMessage = '';
    this.resetFileInput();
    document.body.classList.remove('overflow-hidden');
    this.cdr.markForCheck();
  }

  isOpen = (): boolean => this.isVisible;

  ngOnDestroy(): void {
    document.body.classList.remove('overflow-hidden');
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    Array.from(input.files).forEach((file) => {
      if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
        console.warn(`File "${file.name}" không phải hình ảnh hoặc video.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        this.selectedFiles = [
          ...this.selectedFiles,
          {
            file,
            preview: reader.result as string,
            name: file.name,
            size: file.size,
            type: file.type,
            comment: '',
          },
        ];

        this.cdr.markForCheck();
      };

      reader.readAsDataURL(file);
    });

    this.resetFileInput();
  }

  removeFile(index: number): void {
    if (index < 0 || index >= this.selectedFiles.length) return;

    this.selectedFiles = this.selectedFiles.filter((_, i) => i !== index);
    this.cdr.markForCheck();
  }

  updateComment(index: number, comment: string): void {
    if (index < 0 || index >= this.selectedFiles.length) return;

    this.selectedFiles = this.selectedFiles.map((item, i) =>
      i === index ? { ...item, comment } : item
    );

    this.cdr.markForCheck();
  }

  upload(): void {
    if (this.selectedFiles.length === 0 || this.uploading) return;

    this.uploading = true;
    this.errorMessage = '';
    this.cdr.markForCheck();

    const requests: Observable<UploadApiResponse>[] = this.selectedFiles.map((item) => {
      const description = this.buildDescription(item.comment, this.selectedPrivacy);
      return this.uploadService.uploadFile(item.file, description) as Observable<UploadApiResponse>;
    });

    forkJoin(requests).subscribe({
      next: (responses) => {
        const uploadedFiles = responses
          .map((res) => res?.data)
          .filter((item): item is FileManagerResponse => !!item);

        this.fileUploaded.emit(uploadedFiles);
        this.uploading = false;
        this.close();
      },
      error: (err) => {
        console.error('Lỗi upload:', err);
        this.errorMessage = 'Upload file thất bại. Vui lòng thử lại.';
        this.uploading = false;
        this.cdr.markForCheck();
      },
    });
  }

  trackByIndex(index: number): number {
    return index;
  }

  private buildDescription(comment: string, privacy: Privacy | null): string {
    const normalizedComment = (comment || '').trim();
    const normalizedPrivacy = privacy || '';

    if (normalizedComment && normalizedPrivacy) {
      return JSON.stringify({
        comment: normalizedComment,
        privacy: normalizedPrivacy,
      });
    }

    if (normalizedComment) {
      return normalizedComment;
    }

    if (normalizedPrivacy) {
      return JSON.stringify({
        privacy: normalizedPrivacy,
      });
    }

    return '';
  }

  private resetFileInput(): void {
    if (this.fileInputRef?.nativeElement) {
      this.fileInputRef.nativeElement.value = '';
    }
  }
}